// context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import { setTokens, clearTokens } from "./AxiosInstance";
import apiService from "../Configurations/APIService";
import config from "../Configurations/APIConfig";
import { isPlanExpired } from "../Layout/dateUtils";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // ---------------------------------------------
  // 🧠 Utility Functions
  // ---------------------------------------------

  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const { exp } = jwtDecode(token);
      return exp < Date.now() / 1000;
    } catch {
      return true;
    }
  };

  const saveTokens = (accessToken, refreshToken) => {
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    setTokens({ accessToken, refreshToken });
  };

  const clearStoredTokens = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    clearTokens();
  };

  const mapUserProfile = (profile) => ({
    id: profile.userId,
    cid: profile.companyId,
    bid: profile.branchId,
    role: profile.roleName,
    name: profile.name,
    email: profile.email,
    phoneNumber: profile.phoneNumber,
    country: profile.country,
    currency: profile.currency,
    logo: profile.logo,
    brandName: profile.brandName,
    companyname: profile.companyName,
    companygstno: profile.companyGSTNo,
    companypancard: profile.companyPanCard,
    companyaddress: profile.companyAddress,
    isWebsite: profile.isWebsite,
    connects: profile.connects,
    isRenewal: profile.isRenewal,
    planType: profile.planType,
    endDate: profile.endDate,
    isChatBot: profile.isChatBot,
    whatsAppNumber: profile.whatsAppNumber,
    isSupplierAPI: profile.isSupplierAPI,
    parentCompanyId: profile.parentCompanyId,
  });

  // ---------------------------------------------
  // 🔄 Core Auth Logic
  // ---------------------------------------------

  const fetchUserProfile = async () => {
    const profile = await apiService.getSingle(config.GETUSERPROFILE);
    setUser(mapUserProfile(profile));
    return profile;
  };

  const refreshTokensIfNeeded = async () => {
    let accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) throw new Error("No refresh token found");

    // Refresh only if expired or missing
    if (!accessToken || isTokenExpired(accessToken)) {
      const { accessToken: newAccess, refreshToken: newRefresh } = await apiService.post(
        config.REFRESH,
        { refreshToken }
      );
      saveTokens(newAccess, newRefresh);
      accessToken = newAccess;
      return true; // Token was refreshed
    }

    setTokens({ accessToken, refreshToken });
    return false; // Token was already valid
  };

  const login = async ({ accessToken, refreshToken }) => {
    if (!accessToken || !refreshToken) {
      console.error("Missing access or refresh token");
      return;
    }

    saveTokens(accessToken, refreshToken);

    const profile = await fetchUserProfile();
    navigate("/dashboard");
  };

  const logout = async () => {
    clearStoredTokens();
    setUser(null);
    navigate("/login", { replace: true });
  };

  const refreshUserProfile = async () => {
    console.log("🔄 Refreshing user profile...");
    return await fetchUserProfile();
  };

  // ---------------------------------------------
  // 🚀 Initialization Effect
  // ---------------------------------------------

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      const skipPaths = ["/", "/login", "/unauthorized"];

      if (skipPaths.includes(location.pathname)) {
        if (isMounted) setLoading(false);
        return;
      }

      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        setLoading(false);
        return;
      }

      try {
        const tokenRefreshed = await refreshTokensIfNeeded();

        // Only fetch user profile if:
        // 1. Tokens were refreshed (or)
        // 2. We don't already have user data in state
        if (!user || tokenRefreshed) {
          const profile = await fetchUserProfile();
          if (isMounted) setUser(mapUserProfile(profile));
        }
      } catch (err) {
        console.error("Auth initialization failed:", err);
        if (isMounted) await logout();
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ---------------------------------------------
  // 🧩 Context Provider
  // ---------------------------------------------

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        refreshUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom Hook
export const useAuth = () => useContext(AuthContext);